<template>
  <div class="enhanced-pdf-viewer">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <div class="loading-text">正在加载PDF...</div>
    </div>
    
    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <div class="error-message">{{ error }}</div>
      <div class="error-actions">
        <button @click="retryLoad" class="retry-btn">重试</button>
        <button @click="openInNewTab" class="action-btn primary">新窗口打开</button>
        <button @click="downloadPdf" class="action-btn">下载PDF</button>
      </div>
      <div class="error-tips" v-if="!isMobile()">
        <p>如果预览失败，建议：</p>
        <ul>
          <li>检查网络连接</li>
          <li>刷新页面重试</li>
          <li>直接下载文件查看</li>
        </ul>
      </div>
    </div>
    
    <!-- PDF预览区域 -->
    <div v-else class="pdf-preview-container">
      <!-- 手机端工具栏 -->
      <div class="mobile-toolbar" v-if="isMobile()">
        <button @click="openInNewTab" class="mobile-btn primary">
          <span class="btn-icon">🔗</span>
          新窗口打开
        </button>
        <button @click="downloadPdf" class="mobile-btn">
          <span class="btn-icon">📥</span>
          下载
        </button>
      </div>
      
      <!-- 桌面端模式切换按钮 -->
      <div class="mode-switcher" v-if="!isMobile()">
        <button 
          @click="switchViewMode('iframe')" 
          :class="{ active: viewMode === 'iframe' }"
          class="mode-btn"
        >直接预览</button>
        <button 
          @click="switchViewMode('google')" 
          :class="{ active: viewMode === 'google' }"
          class="mode-btn"
        >Google预览</button>
      </div>
      
      <!-- PDF预览内容 -->
      <div class="pdf-content">
        <!-- 手机端：优先使用新窗口打开 -->
        <div v-if="isMobile()" class="mobile-pdf-placeholder">
          <div class="placeholder-content">
            <div class="pdf-icon">📄</div>
            <h3>PDF文档</h3>
            <p>为了获得最佳阅读体验，建议在新窗口中打开PDF文档</p>
            <button @click="openInNewTab" class="open-pdf-btn">
              打开PDF文档
            </button>
          </div>
        </div>
        
        <!-- 桌面端：iframe预览 -->
        <iframe
          v-else-if="viewMode === 'iframe'"
          :src="directPdfUrl"
          class="pdf-iframe"
          frameborder="0"
          allowfullscreen
          @load="onIframeLoad"
          @error="onIframeError"
        ></iframe>
        
        <!-- 桌面端：Google Viewer预览 -->
        <iframe
          v-else-if="viewMode === 'google'"
          :src="googleViewerUrl"
          class="pdf-iframe"
          frameborder="0"
          allowfullscreen
          @load="onIframeLoad"
          @error="onIframeError"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EnhancedPdfViewer',
  props: {
    pdfUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      viewMode: 'iframe', // 'iframe', 'google'
      loadAttempts: 0,
      maxAttempts: 2
    }
  },
  computed: {
    directPdfUrl() {
      if (!this.pdfUrl) return ''
      
      // 如果是代理URL，直接使用
      if (this.pdfUrl.startsWith('/api/proxy-file/')) {
        return this.pdfUrl
      }
      
      // 对于其他URL，添加内联显示参数
      try {
        const url = new URL(this.pdfUrl)
        url.searchParams.set('response-content-disposition', 'inline')
        url.searchParams.set('response-content-type', 'application/pdf')
        return url.toString()
      } catch (error) {
        return this.pdfUrl
      }
    },
    
    googleViewerUrl() {
      if (!this.pdfUrl) return ''
      
      // 获取完整URL用于Google Viewer
      const fullUrl = this.pdfUrl.startsWith('/api/proxy-file/') 
        ? `${window.location.origin}${this.pdfUrl}`
        : this.pdfUrl
      
      return `https://docs.google.com/viewer?url=${encodeURIComponent(fullUrl)}&embedded=true`
    }
  },
  mounted() {
    this.initializePdfViewer()
  },
  
  methods: {
    initializePdfViewer() {
      this.error = null
      this.loadAttempts = 0
      
      // 手机端不需要iframe预览，直接显示占位符
      if (this.isMobile()) {
        this.loading = false
        return
      }
      
      // 桌面端默认使用iframe模式
      this.viewMode = 'iframe'
      this.loading = false
    },
    
    switchViewMode(mode) {
      if (this.viewMode === mode) return
      
      this.loading = true
      this.error = null
      this.viewMode = mode
      
      // 延迟一点时间让新模式加载
      setTimeout(() => {
        this.loading = false
      }, 800)
    },
    
    onIframeLoad() {
      this.loading = false
      this.error = null
      this.loadAttempts = 0
    },
    
    onIframeError() {
      this.loadAttempts++
      
      if (this.loadAttempts < this.maxAttempts) {
        // 自动尝试Google Viewer
        if (this.viewMode === 'iframe') {
          console.warn('iframe加载失败，尝试Google Viewer')
          this.switchViewMode('google')
        } else {
          this.showError()
        }
      } else {
        this.showError()
      }
    },
    
    showError() {
      this.error = 'PDF加载失败，请尝试其他预览模式或下载文件'
      this.loading = false
    },
    
    retryLoad() {
      this.loadAttempts = 0
      this.initializePdfViewer()
    },
    
    openInNewTab() {
      window.open(this.directPdfUrl, '_blank')
    },
    
    downloadPdf() {
      const link = document.createElement('a')
      let downloadUrl = this.directPdfUrl
      
      // 如果URL包含内联参数，替换为下载参数
      if (downloadUrl.includes('response-content-disposition=inline')) {
        downloadUrl = downloadUrl.replace('response-content-disposition=inline', 'response-content-disposition=attachment')
      }
      
      link.href = downloadUrl
      link.download = 'document.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             window.innerWidth <= 768
    }
  }
}
</script>

<style scoped>
.enhanced-pdf-viewer {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1rem;
  color: #666;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-message {
  font-size: 1.1rem;
  color: #dc3545;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.error-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}

.retry-btn, .action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-btn {
  background: #007bff;
  color: white;
}

.retry-btn:hover {
  background: #0056b3;
}

.action-btn {
  background: #6c757d;
  color: white;
}

.action-btn:hover {
  background: #545b62;
}

.action-btn.primary {
  background: #007bff;
}

.action-btn.primary:hover {
  background: #0056b3;
}

.error-tips {
  margin-top: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: left;
  max-width: 400px;
}

.error-tips p {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: #495057;
}

.error-tips ul {
  margin: 0;
  padding-left: 1.5rem;
}

.error-tips li {
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.pdf-preview-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
}

.mode-switcher {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1002;
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mode-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-btn:hover {
  background: #f8f9fa;
  border-color: #007bff;
}

.mode-btn.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pdf-embed {
  width: 100%;
  height: 100%;
  border: none;
  flex: 1;
  min-height: 0;
}

/* 手机端工具栏 */
.mobile-toolbar {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  justify-content: center;
}

.mobile-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #6c757d;
  color: white;
  min-width: 120px;
  justify-content: center;
}

.mobile-btn.primary {
  background: #007bff;
}

.mobile-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mobile-btn.primary:hover {
  background: #0056b3;
}

.btn-icon {
  font-size: 1.1rem;
}

/* 手机端PDF占位符 */
.mobile-pdf-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.placeholder-content {
  text-align: center;
  max-width: 300px;
}

.pdf-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.placeholder-content h3 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.5rem;
  font-weight: 600;
}

.placeholder-content p {
  margin: 0 0 2rem 0;
  color: #6c757d;
  line-height: 1.6;
  font-size: 1rem;
}

.open-pdf-btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  background: #007bff;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.open-pdf-btn:hover {
  background: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.pdf-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .enhanced-pdf-viewer {
    height: 100%;
    background: #ffffff;
  }
  
  .mobile-toolbar {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .mobile-btn {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    min-width: 100px;
  }
  
  .mobile-pdf-placeholder {
    padding: 1.5rem;
  }
  
  .placeholder-content {
    max-width: 280px;
  }
  
  .pdf-icon {
    font-size: 3rem;
  }
  
  .placeholder-content h3 {
    font-size: 1.3rem;
  }
  
  .placeholder-content p {
    font-size: 0.9rem;
  }
  
  .open-pdf-btn {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
  }
  
  .error-container {
    padding: 1.5rem;
  }
  
  .error-actions {
    gap: 0.5rem;
  }
  
  .action-btn {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .mobile-toolbar {
    padding: 0.5rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .mobile-btn {
    width: 100%;
    padding: 0.75rem;
  }
  
  .mobile-pdf-placeholder {
    padding: 1rem;
  }
  
  .placeholder-content {
    max-width: 250px;
  }
  
  .pdf-icon {
    font-size: 2.5rem;
  }
  
  .placeholder-content h3 {
    font-size: 1.2rem;
  }
  
  .open-pdf-btn {
    width: 100%;
    padding: 1rem;
  }
  
  .error-container {
    padding: 1rem;
  }
  
  .error-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .retry-btn, .action-btn {
    width: 100%;
  }
}
</style>